import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/podkit/tables/Table";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import type { PlainEnvironment } from "@/queries/environment-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { type EnvironmentSpec_Secret } from "gitpod-next-api/gitpod/v1/environment_pb";
import { type Secret } from "gitpod-next-api/gitpod/v1/secret_pb";
import { useCallback, useMemo, type FC } from "react";

type Props = {
    environment: PlainEnvironment;
    userSecrets: PlainMessage<Secret>[];
};

export const EnvironmentSecretsTable: FC<Props> = ({ environment, userSecrets }) => {
    const secretsBySource = useMemo(() => {
        const result = {
            user: [] as PlainMessage<EnvironmentSpec_Secret>[],
            project: [] as PlainMessage<EnvironmentSpec_Secret>[],
        };
        if (!userSecrets) {
            return result;
        }

        for (const secret of environment.spec?.secrets || []) {
            if (userSecrets.some((s) => s.id === secret.sourceRef)) {
                result.user.push(secret);
            } else {
                result.project.push(secret);
            }
        }
        return result;
    }, [environment.spec?.secrets, userSecrets]);
    const specSecretsCount = secretsBySource.user.length + secretsBySource.project.length;

    if (!specSecretsCount) {
        return (
            <div data-testid="environment-secrets-empty-state">
                <Text className="text-base text-content-tertiary">This environment has no secrets.</Text>
            </div>
        );
    }

    return (
        <Table className="table-fixed" data-testid="environment-secrets-table">
            <TableHeader className="h-10 [&_th:last-child]:pl-0 [&_th:last-child]:pr-2 [&_th]:border-border-base [&_th]:border-b-[0.5] [&_th]:bg-transparent [&_th]:px-2 [&_th]:py-1">
                <TableRow>
                    <TableHead className="w-[200px] text-sm font-bold text-content-secondary">Name</TableHead>
                    <TableHead />
                    <TableHead className="w-[34px]" />
                </TableRow>
            </TableHeader>
            <TableBody>
                {secretsBySource.user.map((secret, i) => (
                    <EnvironmentSecretsRow key={`secret-${secret.session}-${i}`} secret={secret} isUser={true} />
                ))}
                {secretsBySource.project.map((secret, i) => (
                    <EnvironmentSecretsRow key={`secret-${secret.session}-${i}`} secret={secret} />
                ))}
            </TableBody>
        </Table>
    );
};

const EnvironmentSecretsRow: FC<{ secret: PlainMessage<EnvironmentSpec_Secret>; isUser?: boolean }> = ({
    secret,
    isUser = false,
}) => {
    const { toast } = useToast();
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyToClipboard = useCallback(() => {
        let copyValue = secret.mount.value;
        if (secret.mount.case === "gitCredentialHost") {
            copyValue = secret.name;
        }
        void navigator.clipboard.writeText(copyValue || "").then(() => {
            setCopied(true);
            toast({ title: "Copied to clipboard" });
        });
    }, [secret.mount.value, secret.mount.case, secret.name, setCopied, toast]);

    let mountLabel = "Environment variable";
    let tooltip = "Copy name";
    if (secret.mount.case === "filePath") {
        mountLabel = secret.mount.value;
        tooltip = "Copy path";
    } else if (secret.mount.case === "containerRegistryBasicAuthHost") {
        mountLabel = `Registry secret (hostname: ${secret.mount.value})`;
        tooltip = "Copy registry hostname";
    }

    return (
        <TableRow
            className="group border-0 hover:bg-surface-02 [&_td:first-child]:rounded-l-md [&_td:first-child]:pl-2 [&_td:last-child]:rounded-r-md [&_td:last-child]:pl-0 [&_td:last-child]:pr-2 [&_td]:px-2 [&_td]:py-1"
            data-testid={`secret-${secret.name}`}
        >
            <TableCell className="p-0">
                <Tooltip content={secret.name}>
                    <Text className="truncate font-mono text-sm font-bold text-content-primary">{secret.name}</Text>
                </Tooltip>
            </TableCell>
            <TableCell className="flex flex-row justify-between">
                <Tooltip content={mountLabel}>
                    <Text className="truncate text-sm text-content-secondary">{mountLabel}</Text>
                </Tooltip>
                {isUser && <UserSecretPill />}
            </TableCell>
            <TableCell>
                <Tooltip content={tooltip}>
                    <Button
                        variant="link"
                        size="sm"
                        disabled={copied}
                        LeadingIcon={
                            copied
                                ? (p) => <IconCheck className="text-green-500 hover:text-green-500" {...p} />
                                : IconCopy
                        }
                        className="hidden border-0 p-0 active:outline-0 group-hover:flex"
                        onClick={handleCopyToClipboard}
                        aria-label={tooltip}
                    />
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};

const UserSecretPill: FC = () => {
    return (
        <span
            className={cn(
                "min-w-fit whitespace-nowrap rounded-full border-0.5 border-border-base px-2 py-0 text-xs",
                "bg-surface-orange",
            )}
        >
            User secret
        </span>
    );
};
